import { OrderType } from "@/types";
import { DateTime, Duration } from "luxon";

export const isWithinBusinessHours = (
  dateTime: DateTime,
  businessHours: any,
  timezone: string,
  leadTime: number
): boolean => {
  const day = dateTime.setZone(timezone).toFormat("cccc").toLowerCase();
  const hoursForDay = businessHours?.[day] || [];

  return hoursForDay.some((timeSlot: any) => {
    const rawStart = timeSlot?.start;
    const rawEnd = timeSlot?.end;

    const start = rawStart
      ? DateTime.fromFormat(rawStart.padStart(4, "0"), "HHmm", {
          zone: timezone,
        }).plus({ minutes: leadTime })
      : null;
    const end = rawEnd
      ? DateTime.fromFormat(rawEnd.padStart(4, "0"), "HHmm", {
          zone: timezone,
        })
      : null;

    if (!start || !end || !start.isValid || !end.isValid) {
      return false;
    }

    const adjustedDateTime = dateTime.setZone(timezone);

    return adjustedDateTime >= start && adjustedDateTime <= end;
  });
};

export const calculatePickupTime = (
  currentDateTime: DateTime,
  businessHours: any,
  leadTime: number,
  timezone: string,
  orderType: OrderType
) => {
  const nowInMerchantZone = currentDateTime.setZone(timezone);
  const { deliveryTime, pickupTime } = JSON.parse(
    global?.window?.localStorage.getItem("orderEstimates") || "{}"
  );
  const deliveryDelay =
    orderType === "delivery"
      ? Math.round(
          (new Date(deliveryTime).getTime() - new Date(pickupTime).getTime()) /
            1000 /
            60
        )
      : 0;

  // This happens when order type is changed to delivery but delivery address is not entered
  if (orderType === "delivery" && !deliveryDelay) {
    return "";
  }

  const leadTimeDuration = Duration.fromObject({
    minutes: leadTime + deliveryDelay,
  });
  const targetPickupTime = nowInMerchantZone.plus(leadTimeDuration);
  const today = nowInMerchantZone.startOf("day");
  const tomorrow = today.plus({ days: 1 });
  const sessionTime = JSON.parse(
    global?.window?.localStorage.getItem("scheduledTime") || "{}"
  );

  if (sessionTime?.date?.dateObject && sessionTime?.time?.timeObject) {
    const storedDateTime = DateTime.fromJSDate(
      new Date(sessionTime.time.timeObject)
    );
    const isInFuture = storedDateTime > nowInMerchantZone;

    if (isInFuture) {
      return `${sessionTime.date.displayValue}, ${sessionTime.time.displayValue}`;
    }
  }

  const canShowASAP = isWithinBusinessHours(
    targetPickupTime,
    businessHours,
    timezone,
    leadTime
  );

  if (canShowASAP) {
    global?.window?.localStorage.setItem(
      "scheduledTime",
      JSON.stringify({
        orderType: orderType,
        asap: true,
        date: {
          displayValue: "Today",
          dateObject: nowInMerchantZone.toJSDate(),
        },
        time: {
          displayValue: "",
          timeObject: nowInMerchantZone.toJSDate(),
        },
      })
    );
    return {
      type: "asap",
      epoch: targetPickupTime.toMillis(),
    };
  }

  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  for (let i = 0; i < days.length; i++) {
    const dayIndex = (nowInMerchantZone.weekday + i) % 7;
    const day = days[dayIndex];
    const hoursForDay = businessHours?.[day];

    if (hoursForDay?.length > 0) {
      for (const slot of hoursForDay) {
        const start = DateTime.fromFormat(slot.start.padStart(4, "0"), "HHmm", {
          zone: timezone,
        }).set({
          year: nowInMerchantZone.year,
          month: nowInMerchantZone.month,
          day: nowInMerchantZone.day + i,
        });

        const end = DateTime.fromFormat(slot.end.padStart(4, "0"), "HHmm", {
          zone: timezone,
        }).set({
          year: nowInMerchantZone.year,
          month: nowInMerchantZone.month,
          day: nowInMerchantZone.day + i,
        });

        const adjustedEnd = end < start ? end.plus({ days: 1 }) : end;

        let availableStart =
          orderType === "delivery"
            ? start.plus(leadTimeDuration)
            : start.plus(leadTimeDuration).plus({ minutes: 15 });

        const minute = availableStart.minute;
        const remainder = minute % 15;
        if (remainder !== 0) {
          availableStart = availableStart.plus({ minutes: 15 - remainder });
        }

        if (
          availableStart >= targetPickupTime &&
          availableStart < adjustedEnd
        ) {
          const scheduleDate = {
            displayValue: availableStart.toFormat("MMM d"),
            dateObject: availableStart.toJSDate(),
          };
          const scheduleTime = {
            displayValue: availableStart.toFormat("h:mm a"),
            timeObject: availableStart.toJSDate(),
          };
          const isToday = availableStart.hasSame(today, "day");
          const isTomorrow = availableStart.hasSame(tomorrow, "day");

          global?.window?.localStorage.setItem(
            "scheduledTime",
            JSON.stringify({
              orderType: sessionTime?.orderType,
              date: scheduleDate,
              time: scheduleTime,
            })
          );

          return isToday
            ? `Today, ${scheduleTime.displayValue}`
            : isTomorrow
            ? `Tomorrow (${scheduleDate.displayValue}), ${scheduleTime.displayValue}`
            : `${scheduleDate.displayValue}, ${scheduleTime.displayValue}`;
        }
      }
    }
  }

  for (let i = 0; i < days.length; i++) {
    const day = days[(nowInMerchantZone.weekday + i - 1) % 7];
    const hoursForDay = businessHours?.[day];

    if (hoursForDay?.length > 0) {
      const firstSlot = hoursForDay[0];

      let start = DateTime.fromFormat(
        firstSlot.start.padStart(4, "0"),
        "HHmm",
        {
          zone: timezone,
        }
      ).set({
        year: nowInMerchantZone.year,
        month: nowInMerchantZone.month,
        day: nowInMerchantZone.day + i,
      });

      start = start.plus(leadTimeDuration).plus({ minutes: 15 });

      const minute = start.minute;
      const remainder = minute % 15;
      if (remainder !== 0) {
        start = start.plus({ minutes: 15 - remainder });
      }

      const userOffset = DateTime.now().offset;
      const merchantOffset = DateTime.now().setZone(timezone).offset;
      const offsetDifference = (merchantOffset - userOffset) / 60;
      const shiftedStart = start.minus({
        hours: offsetDifference,
      });

      const scheduleDate = {
        displayValue: start.toFormat("MMM d"),
        dateObject: start.toJSDate(),
      };
      const scheduleTime = {
        displayValue: start.toFormat("h:mm a"),
        timeObject: shiftedStart.toJSDate(),
      };
      const isToday = start.hasSame(today, "day");
      const isTomorrow = start.hasSame(tomorrow, "day");

      global?.window?.localStorage.setItem(
        "scheduledTime",
        JSON.stringify({
          orderType: sessionTime?.orderType,
          date: scheduleDate,
          time: scheduleTime,
        })
      );

      return isToday
        ? `Today, ${scheduleTime.displayValue}`
        : isTomorrow
        ? `Tomorrow (${scheduleDate.displayValue}), ${scheduleTime.displayValue}`
        : `${scheduleDate.displayValue}, ${scheduleTime.displayValue}`;
    }
  }

  return null;
};
